import { ChevronLeft, ChevronRight, PlaylistPlay } from '@mui/icons-material';
import { Button, IconButton, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import { type JSX } from 'react';
import { isDeviceMobile } from '#helpers/uaParser.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { Modal } from './index.tsx';

const MODAL_TITLE_HEIGHT = 56;

export const Container = styled('div')();

export const TitleContainer = styled('div')({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
});

export const NavContainer = styled('span')();

export type VideoModalProps = {
  children: JSX.Element;
  hasNext?: boolean;
  hasPrev?: boolean;
  id: string | number;
  onClose: () => void;
  onNext?: () => void;
  onOpenPlaylist?: () => void;
  onPrev?: () => void;
  open: boolean;
  playlist?: boolean;
  title: string;
};

export const VideoModal = ({
  children,
  hasNext,
  hasPrev,
  id,
  onClose,
  onNext,
  onOpenPlaylist,
  onPrev,
  open,
  playlist,
  title,
}: VideoModalProps) => {
  const { uaParser } = useToolbelt();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.between('xs', 'sm'));
  const isMobile = isDeviceMobile(uaParser);
  const isMobileInPortrait = isSmall && isMobile;
  const isMobileInLandscape = globalThis.outerHeight < globalThis.outerWidth && isMobile;
  const availHeight = isMobileInPortrait ? window.screen.availWidth : window.screen.availHeight;

  const fixedPositionStyles = {
    '.MuiDialog-container': {
      backgroundColor: theme.palette.grey[900],
    },
    '.MuiDialog-paper': {
      '@media (orientation: landscape)': {
        bottom: 0,
        position: 'fixed',
        top: 0,
      },
      '@media (orientation: portrait)': {
        height: availHeight,
        position: 'fixed',
        transform: 'rotate(90deg)',
      },
      boxShadow: 'none',
      margin: 0,
      maxHeight: 'initial',
      width: 'auto',
    },
    '.MuiDialogContent-root': {
      overflowY: 'clip',
      padding: 0,
    },
  };

  const containerStyles = {
    margin: '0 auto',
    width: `${String(Math.floor((availHeight - MODAL_TITLE_HEIGHT) * 1.7))}px`,
  };

  return (
    <Modal
      dialogContentSx={{
        display: 'flex',
        flexDirection: 'column-reverse',
      }}
      disableBackdropClick
      id={id}
      onClose={onClose}
      open={open}
      sx={isMobileInPortrait || isMobileInLandscape ? fixedPositionStyles : undefined}
      title={
        <TitleContainer sx={{ paddingLeft: playlist ? '40px' : undefined }}>
          {playlist ? (
            <IconButton
              aria-label="Open playlist"
              onClick={onOpenPlaylist}
              sx={{
                color: theme.palette.grey[100],
                left: 8,
                position: 'absolute',
                top: 8,
              }}
            >
              <PlaylistPlay />
            </IconButton>
          ) : undefined}
          <Typography
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
          {playlist ? (
            <NavContainer>
              <Button color="inherit" disabled={!hasPrev} onClick={onPrev} startIcon={<ChevronLeft />} variant="text">
                Prev
              </Button>
              <Button color="inherit" disabled={!hasNext} endIcon={<ChevronRight />} onClick={onNext} variant="text">
                Next
              </Button>
            </NavContainer>
          ) : undefined}
        </TitleContainer>
      }
    >
      <Container sx={isMobileInPortrait || isMobileInLandscape ? containerStyles : undefined}>{children}</Container>
    </Modal>
  );
};
